import React, { useMemo, useRef, useState } from 'react';
import StickySearchBar from '../shared/StickySearchBar';
import { MoldTableView } from '../MoldTableView';
import { Mold } from '../../types/mold';
import { MoldOrderFirstLevelType } from '../../types/mold';
import { useFilteredMolds } from '../../hooks/useFilteredMolds';
import { useSort } from '../../hooks/useSort';
import { usePagination } from '../../hooks/usePagination';
import { AlertTriangle, CheckCircle2, PlusIcon, Wrench, X } from 'lucide-react';
import { StatusFilterType } from '../MoldList/types';
import StatusFilter from '../MoldList/StatusFilter';
import CryAnimation from './CryAnimation';
import ScrollTopIndicator from './ScrollTopIndicator';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAppState } from '../../context/AppStateContext';
import { getStatusMatcher } from '../../hooks/useStatusCounts';

interface MoldSelectionProps {
  molds: Mold[];
  onNextStep: () => void;
  selectedMolds: string[];
  onSelectedMoldsChange: (moldIds: string[]) => void;
  moldOrderMainType: MoldOrderFirstLevelType | null;
  attentionMode?: boolean;
}

const MoldSelection: React.FC<MoldSelectionProps> = ({
  molds,
  onNextStep,
  selectedMolds,
  onSelectedMoldsChange,
  moldOrderMainType,
  attentionMode = false,
}) => {
  const navigate = useNavigate();
  const { getMoldDisplayFieldValues, getVisibleMoldColumns, getMoldSearchFields } = useAppState();
  const columns = getVisibleMoldColumns();
  
  // Get client-specific search fields and filter enabled ones
  const searchFields = getMoldSearchFields().filter(field => field.enabled !== false);
  const tableRef = useRef<HTMLDivElement>(null);
  // Add scroll handler
  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const summaryRef = React.useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    summaryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const [searchConfig, setSearchConfig] = useState({
    query: '',
    field: 'all',
  });
  // Set default statuses based on order type
  const defaultStatuses: StatusFilterType[] = 
    moldOrderMainType === 'mold_acceptance' 
      ? ['INACTIVE', 'RELEASED_TO_EXTERNAL']
      : moldOrderMainType === 'maintenance' && attentionMode
        ? ['ATTENTION_REQUIRED']
      : ['ALL'];
  const [statusFilters, setStatusFilters] = useState<StatusFilterType[]>(defaultStatuses);
  const [eligibleAttentionOnly, setEligibleAttentionOnly] = useState(attentionMode);
  const [visibleColumns] = useState(
    columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.key]: true,
      }),
      {},
    ),
  );

  const isAttentionFilterActive = statusFilters.includes('ATTENTION_REQUIRED');
  const filterSourceMolds = useMemo(
    () =>
      isAttentionFilterActive && eligibleAttentionOnly
        ? molds.filter((mold) => !mold.openMoldOrder)
        : molds,
    [eligibleAttentionOnly, isAttentionFilterActive, molds],
  );

  // Filter molds based on search and status. In attention mode the user can
  // narrow the table to the actionable population without losing visibility
  // of the total attention count in the summary above.
  const filteredMolds = useFilteredMolds(filterSourceMolds, searchConfig, statusFilters, 'ALL');

  // A service batch must not include a mold that already has an open order.
  // The create container fetches this relationship specifically for this
  // screen, so use it to avoid presenting a duplicate-work shortcut.
  const attentionMolds = useMemo(
    () => molds.filter(getStatusMatcher('ATTENTION_REQUIRED')),
    [molds],
  );
  const attentionBatchMolds = useMemo(
    () => attentionMolds.filter((mold) => !mold.openMoldOrder),
    [attentionMolds],
  );
  const attentionMoldsWithOpenOrder = attentionMolds.length - attentionBatchMolds.length;
  const selectedAttentionCount = attentionBatchMolds.filter((mold) =>
    selectedMolds.includes(mold.id),
  ).length;
  const hasSelectedEveryAttentionMold =
    attentionBatchMolds.length > 0 &&
    selectedAttentionCount === attentionBatchMolds.length;

  // Apply sorting
  const { sortedData } = useSort(filteredMolds);

  // Use shared pagination state
  const {
    currentPage,
    pageSize,
    goToPage,
    setPageSize,
  } = usePagination({ data: sortedData });

  const handleSearch = (query: string, field: string) => {
    setSearchConfig({ query, field });
  };

  const toggleSelection = (moldId: string) => {
    const newSelection = selectedMolds.includes(moldId)
      ? selectedMolds.filter((id) => id !== moldId)
      : [...selectedMolds, moldId];
    onSelectedMoldsChange(newSelection);
  };

  const togglePage = (pageMolds: Mold[]) => {
    const pageIds = pageMolds
      .filter((mold) => !mold.openMoldOrder)
      .map((mold) => mold.id);
    const allSelected = pageIds.every((id) => selectedMolds.includes(id));

    if (allSelected) {
      onSelectedMoldsChange(
        selectedMolds.filter((id) => !pageIds.includes(id)),
      );
    } else {
      const newSelection = [...new Set([...selectedMolds, ...pageIds])];
      onSelectedMoldsChange(newSelection);
    }
  };

  const removeSelectedMold = (moldId: string) => {
    onSelectedMoldsChange(selectedMolds.filter((id) => id !== moldId));
  };

  const addAttentionBatch = () => {
    const batchIds = attentionBatchMolds.map((mold) => mold.id);
    onSelectedMoldsChange([...new Set([...selectedMolds, ...batchIds])]);
    setStatusFilters(['ATTENTION_REQUIRED']);
  };

  const getSelectedMoldObjects = () => {
    return molds.filter((mold) => selectedMolds.includes(mold.id));
  };

  return (
    <div ref={summaryRef} className="space-y-6">
      {/* Selected Molds Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-2 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-medium">
              แม่พิมพ์ที่เลือก ({selectedMolds.length})
            </h3>
            {selectedMolds.length > 0 && (
              <button
                onClick={() => onSelectedMoldsChange([])}
                className="px-4 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-full shadow-sm hover:bg-red-50"
              >
                ล้างทั้งหมด
              </button>
            )}
          </div>
        </div>

        <div className="p-6 h-[300px] overflow-y-auto">
          {selectedMolds.length === 0 ? (
            <div className="text-center h-full flex flex-col items-center justify-center">
              <CryAnimation onClick={scrollToTable} />
              <p className="text-gray-500 mb-2">ยังไม่ได้เลือกแม่พิมพ์</p>
              <p className="text-sm text-gray-400">
                กรุณาเลือกแม่พิมพ์จากตารางด้านล่างเพื่อดำเนินการในขั้นตอนถัดไป
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getSelectedMoldObjects().map((mold) => {
                const { title, subtitle } = getMoldDisplayFieldValues(mold);
                return (
                  <div
                    key={mold.id}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{title}</p>
                      <p className="text-sm text-gray-600">{subtitle}</p>
                    </div>
                    <button
                      onClick={() => removeSelectedMold(mold.id)}
                      className="p-1 hover:bg-gray-200 rounded-full"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end items-center py-2 px-2 sm:px-8 border-t">
          <button
            onClick={() => onNextStep()}
            data-cy="next-step-button"
            disabled={selectedMolds.length === 0}
            className={`w-1/2 sm:w-1/4 px-4 py-2 rounded-full text-xl transition duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 ${selectedMolds.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'animate-bounce shadow-lg bg-sky-500 text-white hover:bg-sky-700'
              }`}
          >
            ถัดไป
          </button>
        </div>
      </div>

      {/* Mold Selection Table */}
      <div ref={tableRef} className="bg-white rounded-lg shadow">
        <div className="p-4">
          {moldOrderMainType === 'maintenance' && (
            <div className="mb-4 overflow-hidden rounded-xl border-2 border-red-200 bg-red-50 shadow-sm" data-testid="attention-maintenance-panel">
              <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-red-100 p-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-950">แม่พิมพ์ถึงรอบดูแล {attentionMolds.length} รายการ</p>
                    <p className="mt-1 text-sm text-red-800">
                      เปิดใบงานได้ {attentionBatchMolds.length} รายการ
                      {attentionMoldsWithOpenOrder > 0 && (
                        <> · มีใบงานกำลังดำเนินการแล้ว {attentionMoldsWithOpenOrder} รายการ</>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-red-700">
                      ระบบจะเลือกเฉพาะรายการที่ยังไม่มีใบงาน จากนั้นคุณยังตรวจและแก้ไขรายละเอียดทีละใบได้ก่อนยืนยัน
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addAttentionBatch}
                  disabled={attentionBatchMolds.length === 0}
                  data-cy="select-attention-maintenance-batch"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {hasSelectedEveryAttentionMold ? (
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Wrench className="h-4 w-4" aria-hidden="true" />
                  )}
                  {hasSelectedEveryAttentionMold
                    ? `เลือกครบ ${attentionBatchMolds.length} รายการแล้ว`
                    : `เลือก ${attentionBatchMolds.length} รายการเพื่อสร้างใบงาน`}
                </button>
              </div>
            </div>
          )}
          <StickySearchBar
            onSearch={handleSearch}
            enableScan={true}
            onSearchFieldChange={() => { }}
            searchFields={searchFields} />
          <div className="mt-4 flex flex-col gap-2 sm:gap-0 sm:flex-row justify-between items-center">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
              <StatusFilter
                molds={molds}
                onStatusChange={setStatusFilters}
                className=''
                selectedStatuses={statusFilters}
                isMultiSelect={moldOrderMainType === 'mold_acceptance' || moldOrderMainType === 'mold_export'}
              />
              {moldOrderMainType === 'maintenance' && isAttentionFilterActive && (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
                  <input
                    type="checkbox"
                    checked={eligibleAttentionOnly}
                    onChange={(event) => setEligibleAttentionOnly(event.target.checked)}
                    className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  เฉพาะที่เปิดใบงานได้ ({attentionBatchMolds.length})
                </label>
              )}
            </div>
            {moldOrderMainType === 'mold_acceptance' && (
              <div className="ml-0 sm:ml-4 group relative flex items-center">
                <button
                  onClick={() => navigate(ROUTES.CREATE_NEW_MOLD)}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-sky-400 to-blue-600 text-white rounded-full shadow-lg hover:from-sky-500 hover:to-blue-700 transition-all duration-300 font-semibold text-base focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-opacity-50"
                >
                  <PlusIcon className="w-5 h-5" />
                  สร้างแม่พิมพ์ใหม่
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-40 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-3 py-2 shadow-lg whitespace-nowrap">
                  <p>กรุณาสร้างแม่พิมพ์ใหม่ก่อน</p>
                  <p>เพื่อดำเนินการสร้างใบงานรับเข้าแม่พิมพ์</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <MoldTableView
            molds={sortedData} // Pass the full sorted data for pagination
            visibleColumns={visibleColumns}
            selectedMolds={selectedMolds}
            onSelect={toggleSelection}
            onSelectAll={() => togglePage(sortedData)}
            pagination={true}
            currentPage={currentPage}
            pageSize={pageSize}
            onDataChange={() => { }}
            onPageChange={goToPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      {/* Floating Scroll Indicator */}
      <ScrollTopIndicator
        show={selectedMolds.length > 0}
        onClick={scrollToTop}
        targetRef={summaryRef}
      />
    </div>
  );
};

export default MoldSelection;
